import { zodResolver } from "@hookform/resolvers/zod"
import { Link } from "@tanstack/react-router"
import { signIn } from "aws-amplify/auth"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"
import type { DataRegion, RegionDetails } from "@/types/auth"
import { validateRegionDetails } from "@/utils/region"
import { EmailOTPVerification } from "./email-otp-verification"
import { DataRegionField } from "./forms/fields/auth/data-region"
import { LegalLinks } from "./legal-links"
import { Alert, AlertDescription } from "./ui/alert"
import { Button } from "./ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "./ui/form"
import { Input } from "./ui/input"

interface SignInFormProps {
  redirect?: string
  regionDetails?: RegionDetails
}

export function SignInForm({ redirect, regionDetails }: SignInFormProps = {}) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<"signin" | "verify">("signin")
  const [email, setEmail] = useState("")

  const signInSchema = z.object({
    email: z.string().email({ message: t("auth.errors.invalidEmail") }),
    dataRegion: z.enum(["us", "ca"]),
  })

  // Validate region from regionDetails - only use if it's a valid region
  const validatedRegion = validateRegionDetails(regionDetails)

  const form = useForm<z.infer<typeof signInSchema>>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      dataRegion: validatedRegion as DataRegion,
    },
    reValidateMode: "onSubmit",
  })

  async function onSubmit(values: z.infer<typeof signInSchema>) {
    setIsLoading(true)
    setError(null)

    try {
      await signIn({
        username: values.email,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "EMAIL_OTP",
        },
      })

      setEmail(values.email)
      setStep("verify")
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "An error occurred during sign in")
    } finally {
      setIsLoading(false)
    }
  }

  if (step === "verify") {
    return (
      <EmailOTPVerification
        email={email}
        mode="signin"
        onBack={() => setStep("signin")}
        isLoading={isLoading}
      />
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <FormField
          control={form.control}
          name="dataRegion"
          render={({ field }) => (
            <DataRegionField field={field} regionDetails={regionDetails} mode="signin" />
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("auth.common.emailLabel")}</FormLabel>
              <FormControl>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.common.emailPlaceholder")}
                  required
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? t("auth.common.loading") : t("auth.signIn.loginButton")}
        </Button>

        {/* TODO: Add Google Sign In */}

        {/* <p className="text-center text-sm text-muted-foreground">{t("auth.common.or")}</p>

        <Button variant="outline" className="w-full" type="button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
              fill="currentColor"
            />
          </svg>
          {t("auth.signIn.googleButton")}
        </Button> */}

        <div className="text-center text-sm text-muted-foreground">
          {t("auth.signIn.noAccountText")}{" "}
          <Link
            to="/sign-up"
            search={redirect ? { redirect } : {}}
            className="text-primary hover:underline underline-offset-4"
          >
            {t("auth.signIn.signUpLink")}
          </Link>
        </div>

        <LegalLinks
          termsText={t("auth.common.termsText")}
          termsOfService={t("common.termsOfService")}
          privacyPolicy={t("common.privacyPolicy")}
          dataDeletionAgreement={t("common.dataDeletionAgreement")}
          andText={t("auth.common.and")}
        />
      </form>
    </Form>
  )
}
